import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Sidebar } from './components/layout/Sidebar'
import { Header } from './components/layout/Header'
import { Footer } from './components/layout/Footer'
import { BottomNav } from './components/layout/BottomNav'
import { MobileNav } from './components/layout/MobileNav'
import { UndoToast } from './components/ui/UndoToast'
import { UpdateBanner } from './components/ui/UpdateBanner'
import { useTaskStore } from '@/stores/taskStore'
import { useCategoryStore } from '@/stores/categoryStore'
import { useSettingsStore, applyTheme } from './stores/settingsStore'
import { useWeatherStore } from './stores/weatherStore'
import { useUIStore } from './stores/uiStore'
import { useNotificationStore } from './stores/notificationStore'
import { useUndoStore } from './stores/undoStore'
import { useTemplateStore } from './stores/templateStore'
import { useAuthStore } from './stores/authStore'
import { useProfileStore } from './stores/profileStore'
import { useGroupStore } from './stores/groupStore'
import { useSharedGroupStore } from './stores/sharedGroupStore'
// firestoreSync is dynamically imported to reduce initial bundle
import { AppLoadingScreen } from './components/ui/AppLoadingScreen'
import { AnimatedOutlet } from './components/ui/AnimatedOutlet'
import { useFocusTimer } from './hooks/useFocusTimer'
import { AriaLiveRegion } from './components/ui/AriaLiveRegion'
import { OfflineBanner } from './components/ui/OfflineBanner'
import { SyncErrorBanner } from '@/components/ui/SyncErrorBanner'
import { IOSInstallBanner } from './components/ui/IOSInstallBanner'
import { useGoalConfetti, useStreakConfetti } from './hooks/useConfetti'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import { useThemeOrchestrator } from './hooks/useThemeOrchestrator'
import { useWeather } from './hooks/useWeather'
import { SurvivalModeIndicator } from './components/ui/SurvivalModeIndicator'
import { TimeCapsuleBanner } from './components/ui/TimeCapsuleBanner'
import { ThemeRewardToast } from './components/ui/ThemeRewardToast'
import { NotificationToast } from './components/ui/NotificationToast'
import { GlobalToast } from './components/ui/GlobalToast'
import { useAppBadge } from './hooks/useAppBadge'
import { useAppLock } from './hooks/useAppLock'
import { AppLockScreen } from './components/ui/AppLockScreen'

// Lazy-loaded modals & overlays — 초기 번들에서 제외
const SettingsModal = lazy(() => import('./components/layout/SettingsModal').then(m => ({ default: m.SettingsModal })))
const FAQModal = lazy(() => import('./components/layout/FAQModal').then(m => ({ default: m.FAQModal })))
const TermsModal = lazy(() => import('./components/layout/TermsModal').then(m => ({ default: m.TermsModal })))
const TaskCreateModal = lazy(() => import('./components/tasks/TaskCreateModal').then(m => ({ default: m.TaskCreateModal })))
const WelcomeWizard = lazy(() => import('./components/onboarding/WelcomeWizard').then(m => ({ default: m.WelcomeWizard })))
const FocusMode = lazy(() => import('./components/focus/FocusMode').then(m => ({ default: m.FocusMode })))
const FloatingPIPTimer = lazy(() => import('./components/focus/FloatingPIPTimer').then(m => ({ default: m.FloatingPIPTimer })))
const ConflictResolverModal = lazy(() => import('./components/ui/ConflictResolverModal').then(m => ({ default: m.ConflictResolverModal })))
const KeyboardShortcutModal = lazy(() => import('./components/ui/KeyboardShortcutModal').then(m => ({ default: m.KeyboardShortcutModal })))
const CommandPalette = lazy(() => import('./components/ui/CommandPalette').then(m => ({ default: m.CommandPalette })))

// ─── 탭별 스크롤 위치 기억 ─────────────────────────────
// pathname → 마지막 scrollTop. 의존성 없이 모듈 스코프 Map으로 유지하여
// 라우트 전환 시 메인 스크롤 컨테이너의 위치를 복원한다.
const scrollPositions = new Map<string, number>()
const getMainScroll = (): HTMLElement | null => document.getElementById('main-content')

export default function App() {
  const [isInitialized, setIsInitialized] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)
  const initializeTasks = useTaskStore((state) => state.initialize)
  const initializeCategories = useCategoryStore((state) => state.initialize)
  const initSettings = useSettingsStore((state) => state.initialize)
  const initializeTemplates = useTemplateStore((state) => state.initialize)
  const isSidebarOpen = useUIStore((state) => state.isSidebarOpen)
  const isTaskCreateModalOpen = useUIStore((s) => s.isTaskCreateModalOpen)
  const closeTaskCreateModal = useUIStore((s) => s.closeTaskCreateModal)
  const editingTask = useUIStore((s) => s.editingTask)
  // lastBackupDate is read inside useEffect to avoid hydration timing issues

  // URL ?modal= 로 딥링크되는 전역 모달 플래그 (설정/FAQ/약관)
  const isSettingsModalOpen = useUIStore((s) => s.isSettingsModalOpen)
  const isFAQModalOpen = useUIStore((s) => s.isFAQModalOpen)
  const isTermsModalOpen = useUIStore((s) => s.isTermsModalOpen)
  const openSettingsModal = useUIStore((s) => s.openSettingsModal)
  const closeSettingsModal = useUIStore((s) => s.closeSettingsModal)
  const openFAQModal = useUIStore((s) => s.openFAQModal)
  const closeFAQModal = useUIStore((s) => s.closeFAQModal)
  const openTermsModal = useUIStore((s) => s.openTermsModal)
  const closeTermsModal = useUIStore((s) => s.closeTermsModal)

  // Fire confetti when daily goal is reached or streak milestone hit
  useGoalConfetti()
  useStreakConfetti()

  // Theme overlay orchestration (survival, time capsule, gamification)
  useThemeOrchestrator()
  useWeather()

  const isOnline = useOnlineStatus()

  // Drive the focus timer at App level so it ticks even when the overlay is minimized
  useFocusTimer()

  // App badge — 안 읽은 채팅 수 표시
  useAppBadge()

  // App lock — 생체인증 잠금
  const { isLocked, unlock } = useAppLock()

  // M2: useRef guard to prevent StrictMode double initialization
  const initRef = useRef(false)

  useEffect(() => {
    if (initRef.current) return
    initRef.current = true

    const initApp = async () => {
      try {
        // Parallel initialization of independent stores
        await Promise.all([
          initializeTasks(),
          initializeCategories(),
          initializeTemplates(),
        ])
        // Wait for settings hydration before initializing
        if (!useSettingsStore.getState()._hasHydrated) {
          await new Promise<void>((resolve) => {
            const unsub = useSettingsStore.subscribe((state) => {
              if (state._hasHydrated) { unsub(); resolve() }
            })
            if (useSettingsStore.getState()._hasHydrated) { unsub(); resolve() }
          })
        }
        // 날씨 스토어 hydration도 대기 (일출/일몰 기반 테마 적용 시 레이스 방지)
        if (!useWeatherStore.persist.hasHydrated()) {
          await new Promise<void>((resolve) => {
            useWeatherStore.persist.onFinishHydration(() => resolve())
            if (useWeatherStore.persist.hasHydrated()) resolve()
          })
        }
        initSettings()

        // Register sync refresh callbacks & initialize auth
        const { registerRefreshCallbacks } = await import('./services/firestoreSync')
        registerRefreshCallbacks(
          () => useTaskStore.getState().refreshFromDb(),
          () => useCategoryStore.getState().refreshFromDb(),
          () => useGroupStore.getState().refreshFromDb(),
        )
        useAuthStore.getState().initialize()

        // Auto-reconnect Google Services + start sync if enabled
        if (useSettingsStore.getState().settings.googleCalendarEnabled) {
          import('./services/googleDrive').then(async ({ tryAutoReconnect }) => {
            const result = await tryAutoReconnect()
            if (result === 'ok') {
              const { startGoogleTasksSync } = await import('./services/googleTasksSync')
              startGoogleTasksSync()
            } else if (result === 'no-token') {
              // 토큰 자체가 없음 — 연결한 적 없거나 이미 정리된 상태이므로 설정만 리셋
              useSettingsStore.getState().toggleFeature('googleCalendarEnabled')
            } else {
              // 'expired' — 토큰이 있었지만 만료/갱신 실패
              const { useToastStore } = await import('./stores/toastStore')
              useToastStore.getState().showToast('Google 연결이 만료되었습니다. 설정에서 다시 연결하세요.', 'warning')
            }
          }).catch(console.error)
        }

        // Cleanup expired notifications and check backup reminder
        const notificationStore = useNotificationStore.getState()
        notificationStore.cleanupExpired()
        const currentLastBackupDate = useSettingsStore.getState().settings.lastBackupDate
        notificationStore.checkBackupReminder(currentLastBackupDate)

        // Check task due/overdue notifications
        notificationStore.checkTaskDueNotifications()
        notificationStore.checkOverdueNotifications()

        // Initialize profile (streak calculation)
        useProfileStore.getState().initialize()

        // Initialize groups
        useGroupStore.getState().initialize()
      } catch (err) {
        console.error('App initialization failed:', err)
        setInitError(err instanceof Error ? err.message : '앱 초기화에 실패했습니다')
      } finally {
        setIsInitialized(true)
      }
    }

    initApp()

    // FCM foreground message listener — 인증 상태 변경에 반응
    let fcmUnsubscribe: (() => void) | null = null
    // 비동기 import가 진행 중에 재설정/언마운트되면 이전 리스너가 누수되므로 generation으로 무효화
    let fcmSetupGen = 0

    const setupFCMListener = () => {
      fcmUnsubscribe?.()
      fcmUnsubscribe = null
      const myGen = ++fcmSetupGen
      const user = useAuthStore.getState().user
      const fcmEnabled = useSettingsStore.getState().settings.fcmPushEnabled
      console.info('[FCM-DEBUG] setupFCMListener:', { uid: user?.uid, fcmEnabled, permission: typeof Notification !== 'undefined' ? Notification.permission : 'N/A' })
      if (user && fcmEnabled) {
        import('./services/fcmService').then(({ onForegroundMessage }) => {
          console.info('[FCM-DEBUG] onForegroundMessage 리스너 등록 완료')
          const unsub = onForegroundMessage((payload) => {
            console.info('[FCM-DEBUG] 메시지 수신:', JSON.stringify(payload.data))
            const { title, body } = payload.notification ?? {}
            const data = payload.data ?? {}

            // 현재 채팅방에 있으면 해당 그룹의 채팅 알림 HUN 억제 (카카오톡 동작)
            const currentPath = window.location.pathname
            const isChatType = data.type === 'chat-message' || data.type === 'chat-mention'
            const isInTargetChat = isChatType && data.groupId
              && (currentPath === `/groups/shared/${data.groupId}/chat`
                || (currentPath.startsWith(`/groups/shared/${data.groupId}`) && document.visibilityState === 'visible'))
            if (isInTargetChat) { console.info('[FCM-DEBUG] 억제: 현재 채팅방에 있음', currentPath); return }

            // 뮤트된 그룹의 채팅 알림 억제
            if (isChatType && data.groupId) {
              const members = useSharedGroupStore.getState().activeGroupMembers
              const me = members.find((m: { uid: string }) => m.uid === useAuthStore.getState().user?.uid)
              if (me?.isMuted) { console.info('[FCM-DEBUG] 억제: 뮤트된 그룹'); return }
            }

            const ns = useNotificationStore.getState()
            const notifType = data.type as import('@/lib/types').NotificationType | undefined
            if (!notifType) { console.info('[FCM-DEBUG] 억제: notifType 없음'); return }
            console.info('[FCM-DEBUG] addNotification 호출:', notifType, title, body)
            ns.addNotification({
              type: notifType,
              title: title || '알림',
              message: body || '',
              taskId: data.taskSyncId ? Number(data.taskSyncId) : undefined,
              groupId: data.groupId || undefined,
              senderPhotoURL: data.senderPhotoURL || undefined,
              groupThumbnailUrl: data.groupThumbnailUrl || undefined,
              senderName: data.senderName || undefined,
            })
          })
          // 이 import가 진행되는 동안 재설정/언마운트되었으면 즉시 정리 (중복 토스트/누수 방지)
          if (myGen !== fcmSetupGen) { unsub() }
          else { fcmUnsubscribe = unsub }
        }).catch(console.error)
      }
    }

    setupFCMListener()
    let prevUid = useAuthStore.getState().user?.uid
    const unsubAuth = useAuthStore.subscribe(() => {
      const uid = useAuthStore.getState().user?.uid
      if (uid !== prevUid) { prevUid = uid; setupFCMListener() }
    })

    // Periodic notification checks (every 30 minutes)
    const notifInterval = setInterval(() => {
      const ns = useNotificationStore.getState()
      ns.checkTaskDueNotifications()
      ns.checkOverdueNotifications()
    }, 30 * 60 * 1000)

    // M2: Re-apply theme on visibility change with debounce (300ms)
    let visibilityTimer: ReturnType<typeof setTimeout>
    let lastNotifCheck = 0
    const NOTIF_CHECK_THROTTLE = 30_000 // 30초 스로틀
    const handleVisibilityChange = () => {
      clearTimeout(visibilityTimer)
      if (document.visibilityState === 'visible') {
        // 탭 복귀 시 알림 재확인 — 30초 스로틀 (빠른 탭 전환 시 IndexedDB 중복 쿼리 방지)
        const now = Date.now()
        if (now - lastNotifCheck >= NOTIF_CHECK_THROTTLE) {
          lastNotifCheck = now
          const ns = useNotificationStore.getState()
          ns.checkTaskDueNotifications()
          ns.checkOverdueNotifications()
        }

        visibilityTimer = setTimeout(() => {
          const { theme, weatherThemeEnabled } = useSettingsStore.getState().settings
          if (theme === 'auto-time' || theme === 'system' || weatherThemeEnabled) {
            applyTheme(theme)
          }
        }, 300)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // SW 메시지 수신 (snooze, complete 등)
    const handleSWMessage = (event: MessageEvent) => {
      const { type, data, taskSyncId } = event.data || {}
      if (type === 'SNOOZE_TASK') {
        // Firestore에 nextAlarmAt 재설정 → Cloud Function이 1시간 후 재전송
        const syncId = data?.taskSyncId as string | undefined
        if (syncId) {
          const snoozeTime = new Date(Date.now() + 60 * 60 * 1000).toISOString()
          import('./services/firestoreSync').then(({ resetAlarmForSnooze }) => {
            resetAlarmForSnooze(syncId, snoozeTime).catch(console.error)
          }).catch(console.error)
        }
      } else if (type === 'COMPLETE_TASK' && taskSyncId) {
        // 알림에서 "완료" 클릭 시 작업 완료 처리
        import('./services/database').then(({ getTaskBySyncId }) => {
          getTaskBySyncId(taskSyncId).then((task) => {
            if (task?.id) {
              useTaskStore.getState().toggleComplete(task.id)
            }
          })
        })
      }
    }
    navigator.serviceWorker?.addEventListener('message', handleSWMessage)

    return () => {
      clearInterval(notifInterval)
      clearTimeout(visibilityTimer)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      fcmSetupGen++ // 진행 중인 import 무효화 → 언마운트 후 리스너 등록 방지
      fcmUnsubscribe?.()
      unsubAuth()
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage)
    }
  }, [])

  const openTaskCreateModal = useUIStore((s) => s.openTaskCreateModal)

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K — Command palette (works in all contexts including input fields)
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        useUIStore.getState().openCommandPalette()
        return
      }

      // Skip shortcuts when user is typing in input fields
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

      // Ctrl+Z — Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        useUndoStore.getState().undo()
      }
      // Ctrl+Y / Ctrl+Shift+Z — Redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        useUndoStore.getState().redo()
      }
      // Ctrl+N — New task
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault()
        openTaskCreateModal()
      }
      // ? — Keyboard shortcut help
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        useUIStore.getState().openKeyboardShortcutModal()
      }
      // Ctrl+1~9 — Category quick filter
      if ((e.ctrlKey || e.metaKey) && e.key >= '1' && e.key <= '9') {
        e.preventDefault()
        const cats = useCategoryStore.getState().categories
        const rootCats = cats.filter(c => !c.parentId).sort((a, b) => a.sortOrder - b.sortOrder)
        const idx = parseInt(e.key) - 1
        if (idx < rootCats.length) {
          useUIStore.getState().setActiveCategoryTab(rootCats[idx].id ?? null)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [openTaskCreateModal])

  // ─── 딥링크 가능한 전역 모달 (설정/FAQ/약관) ─────────────────
  // 이 모달들은 URL 검색 파라미터 ?modal=settings|faq|terms 로 표현되어
  // 새로고침/딥링크에도 유지되고, 브라우저(Android 포함) 뒤로가기로 자연스럽게 닫힌다.
  // 두 개의 수렴하는(convergent) 동기화 effect로 URL ↔ uiStore 플래그를 양방향 동기화한다:
  //   1) URL → 플래그: ?modal 값에 맞춰 모달 플래그를 켜고/끈다 (뒤로/앞으로 포함).
  //   2) 플래그 → URL: 모달을 X/배경으로 닫으면 파라미터를 제거한다.
  // ref로 URL 주도 전환과 플래그 주도 전환을 구분해 무한 진동(oscillation)을 막는다.
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setCurrentView = useUIStore((state) => state.setCurrentView)

  const modalParam = searchParams.get('modal')
  // 유효한 모달 이름만 허용 (알 수 없는 값은 무시하고 아래에서 스트립).
  const VALID_MODALS = ['settings', 'faq', 'terms'] as const
  type ModalName = (typeof VALID_MODALS)[number]
  const validModalParam: ModalName | null =
    modalParam && (VALID_MODALS as readonly string[]).includes(modalParam)
      ? (modalParam as ModalName)
      : null

  // 현재 어떤 모달 플래그가 켜져 있는지 → 모달 이름으로 정규화.
  const activeModalFlag: ModalName | null = isSettingsModalOpen
    ? 'settings'
    : isFAQModalOpen
      ? 'faq'
      : isTermsModalOpen
        ? 'terms'
        : null

  // 이 세션에서 우리가 history 항목을 push 했는지 → 닫을 때 navigate(-1) vs replace 결정.
  const pushedThisSessionRef = useRef(false)
  // 직전에 URL 주도로 플래그를 동기화했는지 → 플래그→URL effect가 되받아치지 않게 가드.
  const syncingFromUrlRef = useRef(false)

  // (1) URL → 플래그 동기화: ?modal 값이 나타나거나/바뀌거나/사라지면 플래그를 일치시킨다.
  // 초기 로드(딥링크/새로고침)와 뒤로/앞으로 내비게이션을 모두 처리한다.
  useEffect(() => {
    if (activeModalFlag === validModalParam) return // 이미 일치 — 아무 것도 하지 않음.
    // URL이 진실의 원천이다. 플래그를 URL에 맞춘다.
    syncingFromUrlRef.current = true
    // URL 주도로 닫히는(또는 다른 모달로 바뀌는) 경우 push 소유권은 더 이상 유효하지 않다.
    // (예: 뒤로가기로 ?modal 제거, 탭 이동으로 파라미터 드롭) → navigate(-1) 재발화 방지.
    pushedThisSessionRef.current = false
    // 먼저 모든 모달을 닫고, URL이 가리키는 모달만 연다.
    if (isSettingsModalOpen && validModalParam !== 'settings') closeSettingsModal()
    if (isFAQModalOpen && validModalParam !== 'faq') closeFAQModal()
    if (isTermsModalOpen && validModalParam !== 'terms') closeTermsModal()
    if (validModalParam === 'settings' && !isSettingsModalOpen) openSettingsModal()
    else if (validModalParam === 'faq' && !isFAQModalOpen) openFAQModal()
    else if (validModalParam === 'terms' && !isTermsModalOpen) openTermsModal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validModalParam])

  // (2) 플래그 → URL 동기화: 모달이 X/배경으로 닫혀(또는 코드로 열려) URL과 어긋나면 URL을 맞춘다.
  useEffect(() => {
    if (activeModalFlag === validModalParam) {
      // 일치 상태 도달 — URL 주도 가드를 해제하고 종료.
      syncingFromUrlRef.current = false
      return
    }
    // 방금 URL 주도로 플래그를 바꾼 직후라면(가드 set), 이 발화는 그 결과이므로 URL을 건드리지 않는다.
    if (syncingFromUrlRef.current) {
      syncingFromUrlRef.current = false
      return
    }

    // 여기까지 왔다면 플래그 주도 전환이다 (사용자가 모달을 열거나 닫음).
    if (activeModalFlag) {
      // 플래그가 켜졌는데 URL에 없음 → ?modal 추가 (history push). 알 수 없던 다른 모달 파라미터는 교체.
      const next = new URLSearchParams(searchParams)
      next.set('modal', activeModalFlag)
      pushedThisSessionRef.current = true
      navigate({ pathname: location.pathname, search: `?${next.toString()}` })
    } else {
      // 플래그가 꺼졌는데 URL에 ?modal 이 남아있음 → 파라미터 제거.
      if (pushedThisSessionRef.current) {
        // 우리가 이 세션에서 항목을 쌓았으면 뒤로가기로 소비 (네이티브 뒤로가기 동작 일치).
        pushedThisSessionRef.current = false
        navigate(-1)
      } else {
        // 딥링크/새로고침으로 진입해 쌓은 항목이 없으면 파라미터만 replace로 제거.
        const next = new URLSearchParams(searchParams)
        next.delete('modal')
        const search = next.toString()
        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModalFlag, validModalParam])

  // 알 수 없는 ?modal 값 스트립: 유효하지 않은 값이 URL에 있고 켜진 모달도 없으면 조용히 제거.
  useEffect(() => {
    if (modalParam !== null && validModalParam === null && activeModalFlag === null) {
      const next = new URLSearchParams(searchParams)
      next.delete('modal')
      const search = next.toString()
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalParam, validModalParam, activeModalFlag])

  // ─── 탭별 스크롤 위치 저장/복원 ───────────────────────
  // 나가는 경로의 scrollTop을 라우트 변경 직전 시점에 저장한다.
  // AnimatePresence mode="wait"의 exit 애니메이션 동안 기존 콘텐츠가
  // 아직 표시되므로 이 layout effect 시점의 scrollTop은 나가는 페이지 값이다.
  const prevPathRef = useRef(location.pathname)
  useLayoutEffect(() => {
    const outgoing = prevPathRef.current
    if (outgoing !== location.pathname) {
      const main = getMainScroll()
      if (main) scrollPositions.set(outgoing, main.scrollTop)
      prevPathRef.current = location.pathname
    }
  }, [location.pathname])

  // 새 페이지가 DOM에 커밋된 직후(진입 애니메이션 시작) 호출되어 스크롤을 복원한다.
  const restorePageScroll = useCallback((pathname: string) => {
    const main = getMainScroll()
    if (main) main.scrollTop = scrollPositions.get(pathname) ?? 0
  }, [])

  useEffect(() => {
    const pathname = location.pathname
    if (pathname === '/' || pathname === '/tasks' || pathname.startsWith('/task/')) {
      setCurrentView('tasks')
    } else if (pathname === '/chat') {
      // 채팅 탭이 하단 내비게이션에서 그룹으로 통합됨 — '/chat' 진입 시 그룹 탭을 하이라이트
      setCurrentView('groups')
    } else if (pathname === '/calendar') {
      setCurrentView('calendar')
    } else if (pathname === '/ai') {
      setCurrentView('ai')
    } else if (pathname.startsWith('/groups')) {
      setCurrentView('groups')
    } else if (pathname === '/profile' || pathname === '/activity') {
      setCurrentView('profile')
    }
  }, [location.pathname, setCurrentView])

  if (!isInitialized) {
    return <AppLoadingScreen />
  }

  // 앱 잠금 화면
  if (isLocked) {
    return <AppLockScreen onUnlock={unlock} />
  }

  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-secondary p-4">
        <div className="text-center space-y-4">
          <p className="text-red-500 dark:text-red-400 font-medium">앱 초기화 중 오류가 발생했습니다</p>
          <p className="text-sm text-caption">{initError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="workspace-shell min-h-[100dvh] lg:h-[100dvh] flex flex-col">
      {/* Offline Banner */}
      {!isOnline && <OfflineBanner />}

      {/* Sync Error Banner — 오프라인 시에는 OfflineBanner가 원인을 설명하므로 중첩 방지 (온라인일 때만) */}
      {isOnline && <SyncErrorBanner />}

      {/* Theme State Precedence Banners */}
      <SurvivalModeIndicator />
      <TimeCapsuleBanner />

      {/* Desktop Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col min-h-0 transition-[margin] duration-300 ${isSidebarOpen ? 'lg:ml-[280px]' : 'lg:ml-20'
          }`}
      >
        <Header />
        <div className="flex-1 flex min-h-0 overflow-hidden">
          <main id="main-content" className="workspace-main flex-1 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-0 overflow-y-auto">
            <AnimatedOutlet onPageEnter={restorePageScroll} />
          </main>
        </div>
        <Footer />
      </div>

      {/* Mobile Bottom Navigation */}
      <BottomNav />

      {/* Mobile Drawer Navigation */}
      <MobileNav />

      {/* Global Modals (lazy-loaded) */}
      <Suspense fallback={null}>
        <TaskCreateModal
          isOpen={isTaskCreateModalOpen}
          onClose={closeTaskCreateModal}
          editTask={editingTask}
        />
        <SettingsModal />
        <WelcomeWizard />
        <FAQModal />
        <TermsModal />
        <KeyboardShortcutModal />
        <CommandPalette />
        <ConflictResolverModal />
      </Suspense>

      {/* Undo Toast */}
      <UndoToast />

      {/* PWA Update Banner */}
      <UpdateBanner />

      {/* iOS Safari Install Guide */}
      <IOSInstallBanner />

      {/* Focus Mode Overlay (lazy-loaded) */}
      <Suspense fallback={null}>
        <FocusMode />
        <FloatingPIPTimer />
      </Suspense>

      {/* Accessibility: Live Region for screen reader announcements */}
      <AriaLiveRegion />

      {/* Theme Reward Toast */}
      <ThemeRewardToast />
      <NotificationToast />
      <GlobalToast />
    </div>
  )
}
